import React from 'react'
import { PayloadActionCreator, createAction } from '@reduxjs/toolkit'
import { call, put, takeEvery } from 'redux-saga/effects'
import {
  ClientService,
  ResponseClient,
  ResponseClientCreditLimitAvailable,
} from '@ads/front-core/api'
import { setCurrentCurrency } from '@ads/front-core/store/reducers/currency/setCurrentCurrencyReducer'
import { apiError } from '@ads/front-core/store/sagas/apiErrorSaga'
import { getCurrencyExchange } from '@ads/front-core/utils'
import { setClient } from '../reducers/client/setClientReducer'
import { setCreditLimitAvailable } from '../reducers/client/setCreditLimitAvailableReducer'
import { banner } from '@ads/front-core/store/sagas/bannerSaga'
import { Trans, lng } from '@ads/front-core/locales'
import { ExternalTextLink } from '@ads/front-core/atoms'
import { EMAIL_SUPPORT } from '@ads/front-core/constants'
import { Text } from '@ads/front-ds'
import { hideBanner } from '@ads/front-core/store/reducers/banner/hideBannerReducer'

const CREDIT_LIMIT_BANNER_ID = 'credit-limit-block'

interface FetchClientDataProps {
  clientId: number
}

type FetchClientDataSaga = ReturnType<
  PayloadActionCreator<FetchClientDataProps, string>
>

export function* sagaFetchClientData(): Generator {
  yield takeEvery(fetchClientData.type, fetchClientDataSaga)
}

export const fetchClientData = createAction<FetchClientDataProps>(
  'saga/fetchClientData',
)

export function* fetchClientDataSaga({
  payload,
}: FetchClientDataSaga): Generator {
  const { clientId } = payload
  try {
    if (!clientId) {
      return
    }

    const responseClient = yield call(ClientService.getClient, { clientId })
    const client = responseClient as ResponseClient

    yield put(setClient(client))
    yield put(
      setCurrentCurrency(getCurrencyExchange(client?.settings?.currency)),
    )

    const creditLimitResponse = yield call(
      ClientService.getCreditLimitAvailable,
      {
        clientId,
      },
    )
    const creditLimit = creditLimitResponse as ResponseClientCreditLimitAvailable

    yield put(setCreditLimitAvailable(creditLimit?.available))

    if (client.dukatTrusted) {
      yield put(
        hideBanner({
          id: CREDIT_LIMIT_BANNER_ID,
        }),
      )
    } else {
      yield put(
        banner({
          id: CREDIT_LIMIT_BANNER_ID,
          title: lng('account:accountHasBeenBlocked'),
          description: (
            <Text>
              <Trans
                i18nKey="account:accountHasBeenBlockedDescription"
                values={{
                  EMAIL_SUPPORT,
                }}
                components={{
                  mailto: (
                    <ExternalTextLink
                      href={`mailto:${EMAIL_SUPPORT}`}
                      target="_blank"
                    />
                  ),
                }}
              />
            </Text>
          ),
          variant: 'error',
          disableClose: true,
          disableAutoHide: true,
        }),
      )
    }
  } catch (error) {
    yield put(apiError({ error }))
    if (error?.status === 401) {
      window.localStorage.removeItem('currentClientId')
      window.localStorage.removeItem('currentShopId')
    }
  }
}
